<template>
  <RichTextEditor
    ref="innerEditor"
    v-model="localName"
    class="w-full"
    :disabled="disabled"
    :debounce-updates="!immediate"
    :flush-on-blur="immediate"
    @blur="handleBlur" />
</template>

<script setup lang="ts">
// Per-row buffer around RichTextEditor for answer-option labels. Without this,
// every keystroke writes directly into option.name on the live config tree,
// which restarts the autosave debounce and opens a window for an inbound
// Pusher DraftConfigUpdated event (cross-tab self-echo, or the ENG-1051
// snapshots write source) to refetch and three-way merge over the in-flight
// edit, reverting the field. Uses the same useDebouncedField buffering as the
// shared QuestionEditor fields. (ENG-1052; prior fixes ENG-1014, ENG-1024.)
import type { Segment } from '@dashboard/types/config'
import RichTextEditor from '@dashboard/components/common/RichTextEditor.vue'
import { useDebouncedField } from '@dashboard/composables/useDebouncedField'
import { computed, useTemplateRef } from 'vue'

const props = defineProps<{
  option: Segment
  disabled?: boolean
  immediate?: boolean
}>()

const innerEditor = useTemplateRef<{ editor: any; flush: () => void; discard: () => void; pending: boolean }>(
  'innerEditor'
)

const localName = useDebouncedField(
  () => props.option?.name ?? '',
  (v) => {
    if (props.option) {
      props.option.name = v
    }
  }
)

const editor = computed(() => innerEditor.value?.editor)

function handleBlur() {
  if (props.immediate) {
    flush()
  } else {
    localName.flushNow()
  }
}
function flush() {
  innerEditor.value?.flush()
  localName.flushNow()
}
function discard() {
  innerEditor.value?.discard()
  localName.discard()
}
const isDirty = computed(() => localName.pending.value || !!innerEditor.value?.pending)

defineExpose({ editor, flush, discard, isDirty })
</script>
